import type {
  MembersRequestWithFeatureFlags,
  SearchFilters
} from '@library/types/members';
import { useCallback, useMemo, useState } from 'react';
import { getMembersRequestParams } from '@bo-utils/members/members';
import { useGetMembersQuery } from '@bo-services/members/membersApi';
import { useMembersFeatureFlags } from '@bo-hooks/members';

export const useMembers = ({
  memberSearchFilters
}: {
  memberSearchFilters: Partial<SearchFilters>;
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);

  const featureFlags = useMembersFeatureFlags();

  const isSearchEmpty = useMemo(
    () =>
      Object.entries(memberSearchFilters)
        .filter(([key]) => key !== 'numberOfEntriesType') // Exclude since it will always have a value
        .every(([, value]) => !value),
    [memberSearchFilters]
  );

  const membersRequest: MembersRequestWithFeatureFlags = useMemo(() => {
    const searchfilterParams = getMembersRequestParams(
      currentPage,
      memberSearchFilters
    );

    return {
      ...searchfilterParams,
      featureFlags
    };
  }, [currentPage, featureFlags, memberSearchFilters]);

  const {
    data: membersData,
    isError: isFetchingMembersError,
    isFetching: isFetchingMembers,
    isLoading: isLoadingMembers,
    isSuccess: isFetchingMembersSuccess
  } = useGetMembersQuery(membersRequest, {
    skip: isSearchEmpty,
    refetchOnMountOrArgChange: true
  });

  const isLoadingInitialMembers = useMemo(() => {
    return (isLoadingMembers || isFetchingMembers) && currentPage === 1;
  }, [currentPage, isLoadingMembers, isFetchingMembers]);

  const handleCurrentPageChange = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, []);

  const handleResetCurrentPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    formattedMembers: isSearchEmpty ? undefined : membersData,
    isFetchingMembersError,
    isFetchingMembersSuccess,
    isLoadingMembers: isLoadingInitialMembers,
    onPageChange: handleCurrentPageChange,
    onResetPageChange: handleResetCurrentPage
  };
};
